import React, { useState, useEffect, useMemo } from "react";
import comprasService, { STORAGE_OPEN_CP } from "../../services/comprasService";
import estoqueService from "../../services/estoqueService";
import fornecedoresService from "../../services/fornecedoresService";
import contasContabeisService from "../../services/contasContabeisService";
import pedidosService from "../../services/pedidosService";
import ItemEstoqueAutocomplete from "../../components/estoque/ItemEstoqueAutocomplete";

const fmtMoeda = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtQtd = (v) => Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 3 });
const num = (v) => parseFloat(v) || 0;
const localNome = (l) => l?.nmLocal || l?.nmlocal || `Local #${l?.id}`;

const unidadesOperacionais = (lista, idBase) => {
  if (!idBase) return lista || [];
  return (lista || []).filter(
    (u) => u.ativo !== 0 && (String(u.idUnidadeBase) === String(idBase) || String(u.id) === String(idBase))
  );
};

const calcQtdBase = (qtd, um) => {
  if (!um || qtd === "" || qtd == null) return null;
  const n = parseFloat(qtd);
  if (!Number.isFinite(n)) return null;
  return n * (um.fatorParaBase || 1);
};

const umPadraoItem = (itens, idItem) => {
  const it = (itens || []).find((i) => String(i.id) === String(idItem));
  if (!it) return "";
  return it.idUnidadeCompra || it.idunidadecompra
    || it.idUnidadeOperacional || it.idunidadeoperacional
    || it.idUnidadeBase || it.idunidadebase || "";
};

const siglaBaseItem = (itens, idItem) => {
  const it = (itens || []).find((i) => String(i.id) === String(idItem));
  return it?.siglaUnidadeBase || it?.siglaunidadebase || it?.unidadeMedida || it?.unidademedida || "base";
};

const siglaUm = (unidades, idUm, idItem, itens) => {
  const u = (unidades || []).find((x) => String(x.id) === String(idUm));
  if (u?.sigla) return u.sigla;
  return siglaBaseItem(itens, idItem);
};

const umsDoItem = (unidades, itens, idItem) => {
  const it = (itens || []).find((i) => String(i.id) === String(idItem));
  const idBase = it?.idUnidadeBase || it?.idunidadebase
    || it?.idUnidadeOperacional || it?.idunidadeoperacional || "";
  return unidadesOperacionais(unidades, idBase);
};

const hojeIso = () => new Date().toISOString().split("T")[0];

const EMPTY = {
  idPedido: "", idFornecedor: "", nrDocumento: "", serie: "",
  dtEmissao: hojeIso(),
  dtCompetencia: hojeIso(),
  descricao: "", vlrDesconto: "0", idContaContabil: "", idLocalPadrao: "", observacao: "",
};

const NOVA_LINHA_EMPTY = {
  tipoLinha: "Estoque", idItem: "", quantidade: "", idUnidadeInformada: "",
  vlrUnitario: "", idContaContabil: "", descricaoLinha: "",
};

function gerarParcelas(vlrLiquido, nrParcelas, dtPrimeiro) {
  const n = Math.max(1, parseInt(nrParcelas, 10) || 1);
  const totalCentavos = Math.round(num(vlrLiquido) * 100);
  const baseCentavos = Math.floor(totalCentavos / n);
  const resto = totalCentavos - baseCentavos * n;
  const arr = [];
  const base = dtPrimeiro || new Date().toISOString().split("T")[0];
  for (let i = 0; i < n; i++) {
    const d = new Date(`${base}T12:00:00`);
    d.setMonth(d.getMonth() + i);
    const cents = baseCentavos + (i === n - 1 ? resto : 0);
    arr.push({
      dtVencimento: d.toISOString().split("T")[0],
      vlrParcela: (cents / 100).toFixed(2),
    });
  }
  return arr;
}

export default function NotaFiscalModal({
  show, onClose, onSaved, onNotaCriada, onNavigateFinanceiro,
  notaId, idCondominio, canEdit, saving, setSaving, idPedidoInicial,
}) {
  const [nota, setNota] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [linhas, setLinhas] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [locais, setLocais] = useState([]);
  const [contas, setContas] = useState([]);
  const [itensEstoque, setItensEstoque] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [recebLinhas, setRecebLinhas] = useState([]);
  const [idLocalRec, setIdLocalRec] = useState("");
  const [parcelas, setParcelas] = useState([{ dtVencimento: "", vlrParcela: "" }]);
  const [nrParcelas, setNrParcelas] = useState("1");
  const [dtPrimeiroVenc, setDtPrimeiroVenc] = useState("");
  const [gerarCpAoSalvar, setGerarCpAoSalvar] = useState(false);
  const [secPagamento, setSecPagamento] = useState(true);
  const [secRecebimento, setSecRecebimento] = useState(false);
  const [error, setError] = useState(null);
  const [importandoXml, setImportandoXml] = useState(false);
  const [novaLinha, setNovaLinha] = useState({ ...NOVA_LINHA_EMPTY });

  const readOnly = !canEdit || (nota && ["Recebida", "Cancelada"].includes(nota.status));
  const g = (o, ...keys) => {
    if (o == null) return null;
    return keys.reduce((v, k) => v ?? o[k], null);
  };
  const setF = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const vlrBruto = useMemo(
    () => linhas.reduce((a, l) => a + num(l.quantidade) * num(l.vlrUnitario), 0),
    [linhas]
  );
  const vlrLiquido = Math.max(0, vlrBruto - num(form.vlrDesconto));

  useEffect(() => {
    if (!show || !idCondominio) return;
    fornecedoresService.listar({ idCondominio, situacao: "ativo", limit: 200 }).then((r) => setFornecedores(r.data || []));
    estoqueService.listarLocais({ idCondominio, ativo: "1" }).then((r) => setLocais(r.data || []));
    estoqueService.listarItens({ idCondominio, ativo: "1", limit: 500 }).then((r) => setItensEstoque(r.data || []));
    estoqueService.listarUnidadesMedida({ idCondominio }).then((r) => setUnidades(r.data || []));
    contasContabeisService.listar({ idCondominio, situacao: "ativo", natureza: "Analitica" }).then((r) => setContas(r.data || []));
    pedidosService.listar({ idCondominio, limit: 50 }).then((r) => setPedidos(r.data || []));
  }, [show, idCondominio]);

  const resetModalState = () => {
    const hoje = hojeIso();
    setNota(null);
    setForm({ ...EMPTY, dtEmissao: hoje, dtCompetencia: hoje });
    setLinhas([]);
    setNovaLinha({ ...NOVA_LINHA_EMPTY });
    setRecebLinhas([]);
    setIdLocalRec("");
    setParcelas([{ dtVencimento: hoje, vlrParcela: "0" }]);
    setNrParcelas("1");
    setDtPrimeiroVenc(hoje);
    setGerarCpAoSalvar(false);
    setSecPagamento(true);
    setSecRecebimento(false);
    setError(null);
    setImportandoXml(false);
  };

  useEffect(() => {
    if (!show) {
      resetModalState();
      return;
    }
    setError(null);
    setSecRecebimento(false);
    setGerarCpAoSalvar(false);
    if (notaId) {
      comprasService.buscarPorId(notaId).then((r) => {
        const n = r.data;
        setNota(n);
        const dtEm = (g(n, "dtEmissao", "dtemissao") || "").split("T")[0];
        setForm({
          idPedido: g(n, "idPedido", "idpedido") || "",
          idFornecedor: g(n, "idFornecedor", "idfornecedor") || "",
          nrDocumento: g(n, "nrDocumento", "nrdocumento") || "",
          serie: n.serie || "",
          dtEmissao: dtEm,
          dtCompetencia: (g(n, "dtCompetencia", "dtcompetencia") || dtEm).split("T")[0],
          descricao: n.descricao || "",
          vlrDesconto: String(g(n, "vlrDesconto", "vlrdesconto") ?? 0),
          idContaContabil: g(n, "idContaContabil", "idcontacontabil") || "",
          idLocalPadrao: g(n, "idLocalPadrao", "idlocalpadrao") || "",
          observacao: n.observacao || "",
        });
        const its = (n.itens || []).map((it) => ({
          id: it.id,
          tipoLinha: g(it, "tipoLinha", "tipolinha") || "Estoque",
          idItem: g(it, "idItem", "iditem") || "",
          cdItem: it.cdItem || it.cditem,
          nmItem: it.nmItem || it.nmitem,
          quantidade: String(it.quantidade),
          quantidadeRecebida: num(it.quantidadeRecebida ?? it.quantidaderecebida),
          vlrUnitario: String(it.vlrUnitario ?? it.vlrunitario ?? 0),
          idContaContabil: g(it, "idContaContabil", "idcontacontabil") || "",
          descricaoLinha: it.descricaoLinha || it.descricaolinha || "",
        }));
        setLinhas(its);
        setRecebLinhas(its.filter((l) => l.tipoLinha === "Estoque").map((l) => ({
          idLinha: l.id,
          quantidadeReceber: String(Math.max(0, parseFloat(l.quantidade) - l.quantidadeRecebida)),
          idUnidadeInformada: "",
        })));
        setIdLocalRec(g(n, "idLocalPadrao", "idlocalpadrao") || "");
        const liq = g(n, "vlrLiquido", "vlrliquido") || 0;
        const dtV = dtEm || new Date().toISOString().split("T")[0];
        setDtPrimeiroVenc(dtV);
        setParcelas([{ dtVencimento: dtV, vlrParcela: String(liq) }]);
        setSecPagamento(!(g(n, "idContaPagar", "idcontapagar")));
      }).catch((e) => setError(e.message));
    } else {
      const hoje = hojeIso();
      setNota(null);
      setForm({ ...EMPTY, idPedido: idPedidoInicial || "", dtEmissao: hoje, dtCompetencia: hoje });
      setLinhas([]);
      setNovaLinha({ ...NOVA_LINHA_EMPTY });
      setRecebLinhas([]);
      setIdLocalRec("");
      setDtPrimeiroVenc(hoje);
      setParcelas([{ dtVencimento: hoje, vlrParcela: "0" }]);
      setNrParcelas("1");
      setSecPagamento(true);
    }
  }, [show, notaId, idPedidoInicial]);

  useEffect(() => {
    if (!itensEstoque.length) return;
    setRecebLinhas((prev) => {
      if (!prev.length) return prev;
      let changed = false;
      const next = prev.map((r) => {
        if (r.idUnidadeInformada) return r;
        const lin = linhas.find((l) => l.id === r.idLinha);
        const um = umPadraoItem(itensEstoque, lin?.idItem);
        if (!um) return r;
        changed = true;
        return { ...r, idUnidadeInformada: String(um) };
      });
      return changed ? next : prev;
    });
  }, [itensEstoque, linhas]);

  useEffect(() => {
    if (!show || notaId) return;
    setParcelas((prev) => {
      if (prev.length === 1 && (!prev[0].vlrParcela || prev[0].vlrParcela === "0")) {
        return [{ ...prev[0], vlrParcela: vlrLiquido.toFixed(2) }];
      }
      return prev;
    });
  }, [vlrLiquido, show, notaId]);

  const handleGerarParcelas = () => {
    if (!dtPrimeiroVenc) { setError("Informe a data do primeiro vencimento."); return; }
    setParcelas(gerarParcelas(vlrLiquido, nrParcelas, dtPrimeiroVenc));
    setError(null);
  };

  const salvar = async (opts = {}) => {
    const semVinculo = linhas.filter((l) => l.tipoLinha === "Estoque" && !l.idItem);
    if (semVinculo.length) {
      setError("Linhas de estoque sem item vinculado. Ajuste o cadastro ou remova a linha.");
      return;
    }
    if (!form.descricao?.trim()) { setError("Descrição é obrigatória."); return; }
    if (!linhas.length) { setError("Informe ao menos uma linha na nota."); return; }

    const doGerarCp = opts.gerarCp || (gerarCpAoSalvar && !notaId);
    if (doGerarCp) {
      if (!parcelas.length || !parcelas[0].dtVencimento) {
        setError("Informe a data de vencimento para gerar a conta a pagar.");
        setSecPagamento(true);
        return;
      }
    }

    setSaving(true); setError(null);
    try {
      const payload = {
        ...form,
        idCondominio,
        vlrDesconto: num(form.vlrDesconto),
        itens: linhas.map((l) => ({
          tipoLinha: l.tipoLinha,
          idItem: l.idItem || null,
          quantidade: num(l.quantidade),
          vlrUnitario: num(l.vlrUnitario),
          idContaContabil: l.idContaContabil || null,
          descricaoLinha: l.descricaoLinha || null,
        })),
      };
      let id = notaId;
      if (notaId) await comprasService.atualizar(notaId, payload);
      else {
        const r = await comprasService.criar(payload);
        id = r.data?.id;
      }

      if (doGerarCp && id && !(g(nota, "idContaPagar", "idcontapagar"))) {
        const rCp = await comprasService.gerarContaPagar(id, {
          idContaContabil: form.idContaContabil || null,
          dtEmissao: form.dtEmissao,
          dtCompetencia: form.dtCompetencia,
          parcelas: parcelas.map((p) => ({
            dtVencimento: p.dtVencimento,
            vlrParcela: num(p.vlrParcela),
          })),
        });
        const idCp = rCp.data?.idContaPagar;
        if (idCp) sessionStorage.setItem(STORAGE_OPEN_CP, String(idCp));
        onSaved?.();
        onClose?.();
        if (onNavigateFinanceiro) onNavigateFinanceiro();
        return;
      }

      if (!notaId && id && onNotaCriada) {
        onNotaCriada(id);
        return;
      }
      onSaved?.();
      onClose?.();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const criarDePedido = async () => {
    if (!form.idPedido) { setError("Selecione o pedido."); return; }
    setSaving(true);
    try {
      const r = await comprasService.criarDePedido({
        idPedido: form.idPedido,
        nrDocumento: form.nrDocumento,
        serie: form.serie,
        dtEmissao: form.dtEmissao,
        dtCompetencia: form.dtCompetencia,
        descricao: form.descricao,
      });
      const id = r.data?.id;
      if (id && onNotaCriada) onNotaCriada(id);
      else { onSaved?.(); onClose?.(); }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const importarXmlArquivo = async (file) => {
    if (!file) return;
    setImportandoXml(true); setError(null);
    try {
      const xml = await file.text();
      const r = await comprasService.importarXml({ idCondominio, xml });
      const { cabecalho, itens } = r.data || {};
      if (cabecalho) {
        setForm((p) => ({
          ...p,
          nrDocumento: cabecalho.nrDocumento || p.nrDocumento,
          serie: cabecalho.serie || p.serie,
          dtEmissao: (cabecalho.dtEmissao || p.dtEmissao || "").split("T")[0],
          dtCompetencia: (cabecalho.dtCompetencia || p.dtCompetencia || "").split("T")[0],
          descricao: cabecalho.descricao || p.descricao,
          idFornecedor: cabecalho.idFornecedor ? String(cabecalho.idFornecedor) : p.idFornecedor,
        }));
        if (cabecalho.dtEmissao) setDtPrimeiroVenc(cabecalho.dtEmissao.split("T")[0]);
      }
      if (itens?.length) {
        setLinhas(itens.map((it) => ({
          tipoLinha: it.tipoLinha || "Estoque",
          idItem: it.idItem || "",
          cdItem: it.cdItem,
          nmItem: it.nmItem,
          quantidade: String(it.quantidade),
          quantidadeRecebida: 0,
          vlrUnitario: String(it.vlrUnitario ?? 0),
          descricaoLinha: it.descricaoLinha || "",
          naoVinculado: it.naoVinculado,
        })));
      }
    } catch (e) { setError(e.message); }
    finally { setImportandoXml(false); }
  };

  const cancelarNota = async () => {
    if (!notaId || !window.confirm("Cancelar esta NF? Recebimentos serão estornados do estoque.")) return;
    setSaving(true);
    try {
      await comprasService.cancelar(notaId);
      onSaved?.();
      onClose?.();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const addLinha = () => {
    const it = itensEstoque.find((i) => String(i.id) === String(novaLinha.idItem));
    if (novaLinha.tipoLinha === "Estoque" && !it) return;
    const qInf = num(novaLinha.quantidade);
    if (qInf <= 0) return;

    let quantidade = qInf;
    let quantidadeInformada = qInf;
    let idUnidadeInformada = novaLinha.idUnidadeInformada || "";
    let vlrUnitario = num(novaLinha.vlrUnitario);

    if (novaLinha.tipoLinha === "Estoque") {
      const um = unidades.find((u) => String(u.id) === String(idUnidadeInformada));
      const qBase = um ? calcQtdBase(novaLinha.quantidade, um) : qInf;
      if (!qBase || qBase <= 0) return;
      quantidade = qBase;
      quantidadeInformada = qInf;
      if (!idUnidadeInformada && um) idUnidadeInformada = String(um.id);
      if (vlrUnitario > 0) {
        vlrUnitario = (qInf * vlrUnitario) / qBase;
      }
    }

    setLinhas((p) => [...p, {
      tipoLinha: novaLinha.tipoLinha,
      idItem: novaLinha.idItem,
      cdItem: it?.cdItem,
      nmItem: it?.nmItem || novaLinha.descricaoLinha,
      quantidade,
      quantidadeInformada,
      idUnidadeInformada: idUnidadeInformada || null,
      quantidadeRecebida: 0,
      vlrUnitario,
      idContaContabil: novaLinha.idContaContabil,
      descricaoLinha: novaLinha.descricaoLinha,
    }]);
    setNovaLinha({ ...NOVA_LINHA_EMPTY });
  };

  const umNovaLinha = unidades.find((u) => String(u.id) === String(novaLinha.idUnidadeInformada));
  const qBaseNovaLinha = novaLinha.tipoLinha === "Estoque" && novaLinha.idItem
    ? calcQtdBase(novaLinha.quantidade, umNovaLinha)
    : null;
  const siglaBaseNova = siglaBaseItem(itensEstoque, novaLinha.idItem);
  const umsNovaLinha = umsDoItem(unidades, itensEstoque, novaLinha.idItem);

  const confirmarRecebimento = async () => {
    setSaving(true);
    try {
      await comprasService.receber(notaId, {
        idLocalPadrao: idLocalRec,
        linhas: recebLinhas
          .filter((l) => num(l.quantidadeReceber) > 0)
          .map((l) => ({
            idLinha: l.idLinha,
            quantidadeReceber: l.quantidadeReceber,
            idUnidadeInformada: l.idUnidadeInformada || undefined,
          })),
      });
      onSaved?.();
      const r = await comprasService.buscarPorId(notaId);
      setNota(r.data);
      setSecRecebimento(false);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const gerarCp = async () => {
    if (!parcelas.length || !parcelas[0].dtVencimento) {
      setError("Informe ao menos uma parcela com vencimento.");
      setSecPagamento(true);
      return;
    }
    setSaving(true);
    try {
      const r = await comprasService.gerarContaPagar(notaId, {
        idContaContabil: form.idContaContabil || null,
        dtEmissao: form.dtEmissao,
        dtCompetencia: form.dtCompetencia,
        parcelas: parcelas.map((p) => ({
          dtVencimento: p.dtVencimento,
          vlrParcela: num(p.vlrParcela),
        })),
      });
      const idCp = r.data?.idContaPagar;
      if (idCp) sessionStorage.setItem(STORAGE_OPEN_CP, String(idCp));
      onSaved?.();
      onClose?.();
      if (onNavigateFinanceiro) onNavigateFinanceiro();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (!show) return null;

  const temCp = !!(g(nota, "idContaPagar", "idcontapagar"));
  const linhasEstoque = linhas.filter((l) => l.tipoLinha === "Estoque");
  const pctRecebido = linhasEstoque.length
    ? (linhasEstoque.reduce((a, l) => a + l.quantidadeRecebida, 0)
      / linhasEstoque.reduce((a, l) => a + num(l.quantidade), 0)) * 100
    : 0;
  const podeReceber = notaId && nota?.status !== "Cancelada"
    && linhasEstoque.some((l) => num(l.quantidade) - l.quantidadeRecebida > 0.0001);

  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,.5)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header py-2">
            <div>
              <h5 className="modal-title mb-0">
                {nota ? `Nota fiscal #${nota.id}` : "Nova nota fiscal"}
                {nota?.status && (
                  <span className="badge bg-secondary ms-2" style={{ fontSize: 11 }}>{nota.status}</span>
                )}
              </h5>
              <small className="text-muted">Lançamento — cabeçalho, itens e pagamento na mesma tela</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body pb-0">
            {error && <div className="alert alert-danger py-2 small mb-2">{error}</div>}

            {/* ── Documento ── */}
            <div className="form-section mb-3">
              <div className="form-section-title d-flex flex-wrap justify-content-between align-items-center gap-2">
                <span><i className="bi bi-file-text me-1" /> Documento</span>
                {!notaId && canEdit && (
                  <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-sm btn-outline-primary" disabled={!form.idPedido || saving}
                      onClick={criarDePedido}>
                      Gerar do pedido
                    </button>
                    <label className="btn btn-sm btn-outline-secondary mb-0">
                      <i className="bi bi-file-earmark-code me-1" />
                      {importandoXml ? "Lendo…" : "XML NF-e"}
                      <input type="file" accept=".xml,text/xml,application/xml" className="d-none"
                        disabled={importandoXml}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) importarXmlArquivo(f); e.target.value = ""; }} />
                    </label>
                  </div>
                )}
              </div>
              <div className="row g-2">
                <div className="col-md-4">
                  <label className="form-label small">Fornecedor</label>
                  <select className="form-select form-select-sm" disabled={readOnly}
                    value={form.idFornecedor} onChange={(e) => setF("idFornecedor", e.target.value)}>
                    <option value="">— Selecione —</option>
                    {fornecedores.map((f) => (
                      <option key={f.id} value={f.id}>{f.nmReduzido || f.nmRazaoSocial}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small">Nº NF</label>
                  <input className="form-control form-control-sm" disabled={readOnly}
                    value={form.nrDocumento} onChange={(e) => setF("nrDocumento", e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label small">Série</label>
                  <input className="form-control form-control-sm" disabled={readOnly}
                    value={form.serie} onChange={(e) => setF("serie", e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label small">Data emissão</label>
                  <input type="date" className="form-control form-control-sm" disabled={readOnly}
                    value={form.dtEmissao} onChange={(e) => setF("dtEmissao", e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label small">Competência</label>
                  <input type="date" className="form-control form-control-sm" disabled={readOnly}
                    value={form.dtCompetencia} onChange={(e) => setF("dtCompetencia", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Descrição *</label>
                  <input className="form-control form-control-sm" disabled={readOnly}
                    value={form.descricao} onChange={(e) => setF("descricao", e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label small">Pedido (opcional)</label>
                  <select className="form-select form-select-sm" disabled={!!notaId}
                    value={form.idPedido} onChange={(e) => setF("idPedido", e.target.value)}>
                    <option value="">—</option>
                    {pedidos.map((p) => (
                      <option key={p.id} value={p.id}>{p.nrPedido || p.nrpedido} — {p.status}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small">Conta contábil</label>
                  <select className="form-select form-select-sm" disabled={readOnly}
                    value={form.idContaContabil} onChange={(e) => setF("idContaContabil", e.target.value)}>
                    <option value="">—</option>
                    {contas.map((c) => (
                      <option key={c.id} value={c.id}>{c.nmContaContabil || c.descConta}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Local padrão (estoque)</label>
                  <select className="form-select form-select-sm" disabled={readOnly}
                    value={form.idLocalPadrao} onChange={(e) => setF("idLocalPadrao", e.target.value)}>
                    <option value="">—</option>
                    {locais.map((l) => <option key={l.id} value={l.id}>{localNome(l)}</option>)}
                  </select>
                </div>
                <div className="col-md-8">
                  <label className="form-label small">Observação</label>
                  <input className="form-control form-control-sm" disabled={readOnly}
                    value={form.observacao} onChange={(e) => setF("observacao", e.target.value)} />
                </div>
              </div>
            </div>

            {/* ── Itens ── */}
            <div className="form-section mb-3">
              <div className="form-section-title">
                <i className="bi bi-list-ul me-1" /> Itens da nota
                <span className="text-muted fw-normal ms-2">({linhas.length} linha{linhas.length !== 1 ? "s" : ""})</span>
              </div>
              <p className="small text-muted mb-2">
                Informe a quantidade na <strong>UM da nota</strong> (padrão: UM de compra do item).
                O saldo fica na <strong>unidade base</strong> do item: entrar <strong>1 L</strong> grava <strong>1 L</strong> (não vira 1.000 ML).
                Se digitar em ML, o sistema converte para a base (ex.: 1.000 ML → 1 L quando a base é L).
              </p>
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Tipo</th><th>Item / Despesa</th>
                      <th className="text-end">Qtd</th><th>UM</th>
                      <th className="text-end">Vlr un.</th>
                      <th className="text-end">Total</th>
                      {notaId && <th className="text-end">Recebido (base)</th>}
                      {!readOnly && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l, idx) => {
                      const siglaInf = l.tipoLinha === "Estoque"
                        ? siglaUm(unidades, l.idUnidadeInformada, l.idItem, itensEstoque)
                        : "—";
                      const siglaB = siglaBaseItem(itensEstoque, l.idItem);
                      const qInf = l.quantidadeInformada ?? l.quantidade;
                      const mostraConv = l.tipoLinha === "Estoque"
                        && l.idUnidadeInformada
                        && Math.abs(num(qInf) - num(l.quantidade)) > 0.0001
                        && siglaInf !== siglaB;
                      return (
                      <tr key={l.id || idx}>
                        <td><span className="badge bg-light text-dark border">{l.tipoLinha}</span></td>
                        <td>
                          {l.tipoLinha === "Estoque" ? (
                            <>
                              {l.cdItem ? `${l.cdItem} — ` : ""}{l.nmItem}
                              {l.naoVinculado && <span className="badge bg-warning text-dark ms-1">sem vínculo</span>}
                            </>
                          ) : (l.descricaoLinha || "Despesa")}
                        </td>
                        <td className="text-end">
                          {fmtQtd(qInf)}
                          {mostraConv && (
                            <small className="text-muted d-block">= {fmtQtd(l.quantidade)} {siglaB}</small>
                          )}
                        </td>
                        <td className="text-muted small">{siglaInf}</td>
                        <td className="text-end">
                          {fmtMoeda(l.vlrUnitario)}
                          {l.tipoLinha === "Estoque" && (
                            <span className="text-muted small"> /{siglaB}</span>
                          )}
                        </td>
                        <td className="text-end">{fmtMoeda(num(l.quantidade) * num(l.vlrUnitario))}</td>
                        {notaId && (
                          <td className="text-end text-muted small">
                            {l.tipoLinha === "Estoque"
                              ? <>{fmtQtd(l.quantidadeRecebida)} {siglaB}</>
                              : "—"}
                          </td>
                        )}
                        {!readOnly && (
                          <td className="text-end">
                            <button type="button" className="btn btn-sm btn-outline-danger py-0"
                              onClick={() => setLinhas((p) => p.filter((_, i) => i !== idx))}>
                              <i className="bi bi-trash" />
                            </button>
                          </td>
                        )}
                      </tr>
                    );})}
                    {!linhas.length && (
                      <tr><td colSpan={notaId ? 8 : 7} className="text-center text-muted py-3">Nenhum item</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {!readOnly && (
                <div className="p-2 mt-2 rounded" style={{ background: "var(--bg-highlight)" }}>
                  <div className="row g-2 align-items-end">
                    <div className="col-auto">
                      <select className="form-select form-select-sm" value={novaLinha.tipoLinha}
                        onChange={(e) => setNovaLinha((p) => ({ ...p, tipoLinha: e.target.value }))}>
                        <option value="Estoque">Estoque</option>
                        <option value="Despesa">Despesa</option>
                      </select>
                    </div>
                    {novaLinha.tipoLinha === "Estoque" ? (
                      <div className="col">
                        <ItemEstoqueAutocomplete
                          listId="nf-item-estoque-list"
                          itens={itensEstoque}
                          value={novaLinha.idItem}
                          placeholder="Digite código ou descrição…"
                          onChange={(idItem) => {
                            const um = umPadraoItem(itensEstoque, idItem);
                            setNovaLinha((p) => ({
                              ...p,
                              idItem,
                              idUnidadeInformada: um ? String(um) : "",
                            }));
                          }}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="col">
                          <select className="form-select form-select-sm" value={novaLinha.idContaContabil}
                            onChange={(e) => setNovaLinha((p) => ({ ...p, idContaContabil: e.target.value }))}>
                            <option value="">Conta contábil…</option>
                            {contas.map((c) => (
                              <option key={c.id} value={c.id}>{c.nmContaContabil || c.descConta}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col">
                          <input className="form-control form-control-sm" placeholder="Descrição"
                            value={novaLinha.descricaoLinha}
                            onChange={(e) => setNovaLinha((p) => ({ ...p, descricaoLinha: e.target.value }))} />
                        </div>
                      </>
                    )}
                    {novaLinha.tipoLinha === "Estoque" && (
                      <div className="col-auto">
                        <select
                          className="form-select form-select-sm"
                          style={{ width: 72 }}
                          value={novaLinha.idUnidadeInformada}
                          disabled={!novaLinha.idItem}
                          onChange={(e) => setNovaLinha((p) => ({ ...p, idUnidadeInformada: e.target.value }))}
                        >
                          <option value="">UM</option>
                          {umsNovaLinha.map((u) => (
                            <option key={u.id} value={u.id}>{u.sigla}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="col-auto">
                      <input type="number" step="0.001" className="form-control form-control-sm"
                        placeholder="Qtd" style={{ width: 80 }} value={novaLinha.quantidade}
                        onChange={(e) => setNovaLinha((p) => ({ ...p, quantidade: e.target.value }))} />
                    </div>
                    <div className="col-auto">
                      <input type="number" step="0.01" className="form-control form-control-sm"
                        placeholder={novaLinha.tipoLinha === "Estoque" && umNovaLinha?.sigla
                          ? `R$/${umNovaLinha.sigla}` : "R$ un."}
                        style={{ width: 96 }} value={novaLinha.vlrUnitario}
                        onChange={(e) => setNovaLinha((p) => ({ ...p, vlrUnitario: e.target.value }))} />
                    </div>
                    <div className="col-auto">
                      <button type="button" className="btn btn-sm btn-primary" onClick={addLinha}>
                        <i className="bi bi-plus-lg" />
                      </button>
                    </div>
                  </div>
                  {novaLinha.tipoLinha === "Estoque" && novaLinha.idItem && qBaseNovaLinha != null && (
                    <p className="small text-muted mb-0 mt-1">
                      Entrada no estoque: <strong>{fmtQtd(qBaseNovaLinha)} {siglaBaseNova}</strong>
                      {umNovaLinha && !umNovaLinha.ehBase && novaLinha.quantidade && (
                        <span> (conversão de {fmtQtd(novaLinha.quantidade)} {umNovaLinha.sigla})</span>
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* ── Totais ── */}
            <div className="card border-0 mb-3" style={{ background: "var(--bg-highlight)" }}>
              <div className="card-body py-2">
                <div className="row g-2 align-items-end">
                  <div className="col-md-3">
                    <span className="small text-muted d-block">Valor bruto (itens)</span>
                    <strong>{fmtMoeda(vlrBruto)}</strong>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-0">Desconto</label>
                    <input type="number" step="0.01" min="0" className="form-control form-control-sm"
                      disabled={readOnly} value={form.vlrDesconto}
                      onChange={(e) => setF("vlrDesconto", e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <span className="small text-muted d-block">Valor líquido</span>
                    <strong className="text-primary fs-5">{fmtMoeda(vlrLiquido)}</strong>
                  </div>
                  {notaId && linhasEstoque.length > 0 && (
                    <div className="col-md-3">
                      <span className="small text-muted d-block">Recebimento estoque</span>
                      <div className="progress" style={{ height: 8 }}>
                        <div className="progress-bar bg-success" style={{ width: `${Math.min(100, pctRecebido)}%` }} />
                      </div>
                      <small className="text-muted">{pctRecebido.toFixed(0)}%</small>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Pagamento (colapsável) ── */}
            {canEdit && !temCp && (
              <div className="form-section mb-3">
                <button type="button" className="btn btn-link p-0 form-section-title text-decoration-none"
                  onClick={() => setSecPagamento((v) => !v)}>
                  <i className={`bi bi-chevron-${secPagamento ? "down" : "right"} me-1`} />
                  Pagamento / Conta a pagar
                </button>
                {secPagamento && (
                  <div className="mt-2 ps-1">
                    {!notaId && (
                      <div className="form-check mb-2">
                        <input className="form-check-input" type="checkbox" id="gerarCpSalvar"
                          checked={gerarCpAoSalvar} onChange={(e) => setGerarCpAoSalvar(e.target.checked)} />
                        <label className="form-check-label small" htmlFor="gerarCpSalvar">
                          Gerar conta a pagar ao salvar a NF
                        </label>
                      </div>
                    )}
                    <div className="row g-2 align-items-end mb-2">
                      <div className="col-auto">
                        <label className="form-label small mb-0">1º vencimento</label>
                        <input type="date" className="form-control form-control-sm"
                          value={dtPrimeiroVenc} onChange={(e) => setDtPrimeiroVenc(e.target.value)} />
                      </div>
                      <div className="col-auto">
                        <label className="form-label small mb-0">Nº parcelas</label>
                        <input type="number" min="1" max="24" className="form-control form-control-sm"
                          style={{ width: 70 }} value={nrParcelas}
                          onChange={(e) => setNrParcelas(e.target.value)} />
                      </div>
                      <div className="col-auto">
                        <button type="button" className="btn btn-sm btn-outline-primary"
                          onClick={handleGerarParcelas}>
                          Gerar parcelas
                        </button>
                      </div>
                    </div>
                    {parcelas.map((p, i) => (
                      <div key={i} className="row g-2 mb-1 align-items-center">
                        <div className="col-auto small text-muted">#{i + 1}</div>
                        <div className="col-auto">
                          <input type="date" className="form-control form-control-sm"
                            value={p.dtVencimento}
                            onChange={(e) => {
                              const v = e.target.value;
                              setParcelas((arr) => arr.map((x, j) => (j === i ? { ...x, dtVencimento: v } : x)));
                            }} />
                        </div>
                        <div className="col-auto">
                          <input type="number" step="0.01" className="form-control form-control-sm"
                            style={{ width: 120 }} value={p.vlrParcela}
                            onChange={(e) => {
                              const v = e.target.value;
                              setParcelas((arr) => arr.map((x, j) => (j === i ? { ...x, vlrParcela: v } : x)));
                            }} />
                        </div>
                      </div>
                    ))}
                    {notaId && (
                      <button type="button" className="btn btn-sm btn-primary mt-2" disabled={saving} onClick={gerarCp}>
                        Gerar conta a pagar
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {temCp && (
              <div className="alert alert-success py-2 small">
                Conta a pagar #{g(nota, "idContaPagar", "idcontapagar")} vinculada.{" "}
                {onNavigateFinanceiro && (
                  <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                    onClick={() => {
                      sessionStorage.setItem(STORAGE_OPEN_CP, String(g(nota, "idContaPagar", "idcontapagar")));
                      onNavigateFinanceiro();
                      onClose?.();
                    }}>
                    Abrir em Contas a Pagar
                  </button>
                )}
              </div>
            )}

            {/* ── Recebimento (colapsável) ── */}
            {podeReceber && canEdit && (
              <div className="form-section mb-3">
                <button type="button" className="btn btn-link p-0 form-section-title text-decoration-none"
                  onClick={() => setSecRecebimento((v) => !v)}>
                  <i className={`bi bi-chevron-${secRecebimento ? "down" : "right"} me-1`} />
                  Recebimento no estoque
                </button>
                {secRecebimento && (
                  <div className="mt-2">
                    <div className="mb-2">
                      <label className="form-label small">Local de entrada</label>
                      <select className="form-select form-select-sm" style={{ maxWidth: 280 }}
                        value={idLocalRec} onChange={(e) => setIdLocalRec(e.target.value)}>
                        <option value="">Selecione…</option>
                        {locais.map((l) => <option key={l.id} value={l.id}>{localNome(l)}</option>)}
                      </select>
                    </div>
                    <table className="table table-sm mb-2">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th className="text-end">Pendente (base)</th>
                          <th>UM</th>
                          <th className="text-end">Receber agora</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linhasEstoque.map((l, idx) => {
                          const pend = Math.max(0, num(l.quantidade) - l.quantidadeRecebida);
                          const rec = recebLinhas.find((r) => r.idLinha === l.id);
                          const itEst = itensEstoque.find((i) => String(i.id) === String(l.idItem));
                          const idBase = itEst?.idUnidadeBase || itEst?.idunidadebase || itEst?.idUnidadeOperacional || itEst?.idunidadeoperacional || "";
                          const umsItem = unidadesOperacionais(unidades, idBase);
                          const umSel = unidades.find((u) => String(u.id) === String(rec?.idUnidadeInformada));
                          const siglaB = siglaBaseItem(itensEstoque, l.idItem);
                          const qBasePrev = calcQtdBase(rec?.quantidadeReceber, umSel);
                          return (
                            <tr key={l.id || idx}>
                              <td>{l.cdItem} — {l.nmItem}</td>
                              <td className="text-end">{fmtQtd(pend)} {siglaB}</td>
                              <td>
                                {umsItem.length > 0 ? (
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ width: 72 }}
                                    value={rec?.idUnidadeInformada || ""}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setRecebLinhas((p) => {
                                        const rest = p.filter((x) => x.idLinha !== l.id);
                                        return [...rest, {
                                          idLinha: l.id,
                                          quantidadeReceber: rec?.quantidadeReceber ?? "",
                                          idUnidadeInformada: v,
                                        }];
                                      });
                                    }}
                                  >
                                    {umsItem.map((u) => (
                                      <option key={u.id} value={u.id}>{u.sigla}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span className="text-muted small">{siglaB}</span>
                                )}
                              </td>
                              <td className="text-end">
                                <input type="number" step="0.001" className="form-control form-control-sm text-end"
                                  style={{ width: 100, marginLeft: "auto" }}
                                  value={rec?.quantidadeReceber ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setRecebLinhas((p) => {
                                      const existing = p.find((x) => x.idLinha === l.id);
                                      const rest = p.filter((x) => x.idLinha !== l.id);
                                      return [...rest, {
                                        idLinha: l.id,
                                        quantidadeReceber: v,
                                        idUnidadeInformada: existing?.idUnidadeInformada
                                          || String(umPadraoItem(itensEstoque, l.idItem) || ""),
                                      }];
                                    });
                                  }} />
                                {qBasePrev != null && umSel && (
                                  <small className="text-muted d-block">
                                    = {fmtQtd(qBasePrev)} {siglaB}
                                  </small>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <button type="button" className="btn btn-sm btn-success" disabled={saving}
                      onClick={confirmarRecebimento}>
                      Confirmar recebimento
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer flex-wrap gap-2 border-top sticky-bottom bg-white">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Fechar</button>
            {canEdit && !readOnly && (
              <button type="button" className="btn btn-primary" disabled={saving}
                onClick={() => salvar({ gerarCp: gerarCpAoSalvar && !notaId })}>
                {saving ? "Salvando…" : (gerarCpAoSalvar && !notaId ? "Salvar e gerar CP" : "Salvar NF")}
              </button>
            )}
            {canEdit && notaId && nota?.status !== "Cancelada" && (
              <button type="button" className="btn btn-outline-danger" disabled={saving} onClick={cancelarNota}>
                Cancelar NF
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
